import * as THREE from "three";
import { DOOR_POSITIONS, DoorDirection, DungeonRoom, inferDoorDirectionFromRotation } from "./dungeonTypes";

export interface DungeonSpawnContext {
  fromRoomId?: string | null;
  entryDirection?: DoorDirection | null;
  isChapterStart?: boolean;
}

export interface DungeonEnemySpawnContext {
  roomCompleted?: boolean;
  forceRespawn?: boolean;
}

function clampToRoom(position: THREE.Vector3, boundary: number): THREE.Vector3 {
  const clamped = position.clone();
  clamped.x = Math.max(-boundary, Math.min(boundary, clamped.x));
  clamped.z = Math.max(-boundary, Math.min(boundary, clamped.z));
  clamped.y = 0;
  return clamped;
}

function getInboundExit(
  room: DungeonRoom,
  context: DungeonSpawnContext
): { direction: DoorDirection; anchor?: THREE.Vector3 } | null {
  const entryDirection = context.entryDirection ?? null;
  if (!context.fromRoomId && !entryDirection) return null;

  // === CONTINUIDADE GEOMÉTRICA ===
  // A regra de ouro: se o jogador saiu por uma porta NORTH da sala A,
  // ele deve entrar pela porta SOUTH da sala B (entryDirection = SOUTH).
  // Isso vale SEMPRE, independentemente de qual porta da sala B aponta
  // de volta para A no config. Um config pode ter uma porta de retorno
  // em direção "desalinhada" (ex: a porta de volta em B é WEST em vez
  // de SOUTH) — nesse caso ignoramos a direção do config e preservamos
  // a continuidade geométrica.

  if (entryDirection) {
    // Prioridade 1: porta nessa direção que aponta de volta para a sala de origem.
    if (context.fromRoomId) {
      const perfectMatch = room.exits.find((exit) => {
        if (exit.targetRoomId !== context.fromRoomId) return false;
        const exitDirection = exit.direction ?? inferDoorDirectionFromRotation(exit.rotation);
        return exitDirection === entryDirection;
      });
      if (perfectMatch) {
        return { direction: entryDirection, anchor: perfectMatch.position };
      }
    }

    // Prioridade 2: qualquer porta nessa direção (mesmo que aponte para outra sala).
    // Usamos sua posição como âncora para que o spawn fique alinhado à abertura
    // física da parede (pode estar deslocada lateralmente do centro).
    const sideMatch = room.exits.find((exit) => {
      const exitDirection = exit.direction ?? inferDoorDirectionFromRotation(exit.rotation);
      return exitDirection === entryDirection;
    });
    if (sideMatch) {
      return { direction: entryDirection, anchor: sideMatch.position };
    }

    // Prioridade 3: sem porta na direção esperada — usar posição padrão da
    // parede correspondente via DOOR_POSITIONS. O Arena.tsx injeta uma
    // phantom door nessa direção para permitir travessia física de volta.
    return { direction: entryDirection, anchor: undefined };
  }

  // Sem entryDirection (ex: reload): fallback para qualquer porta que
  // aponte de volta à sala de origem.
  const backExit = context.fromRoomId
    ? room.exits.find((exit) => exit.targetRoomId === context.fromRoomId)
    : null;
  if (!backExit) return null;

  const backDirection = backExit.direction ?? inferDoorDirectionFromRotation(backExit.rotation);
  return { direction: backDirection, anchor: backExit.position };
}

/**
 * Calcula posição de spawn perto de uma porta, com inset para dentro da sala.
 * O `direction` é a direção cardinal da porta (ex: "SOUTH" = porta na parede sul).
 * O offset move o spawn PARA DENTRO da sala (em direção ao centro).
 */
function spawnNearDoor(
  direction: DoorDirection,
  entryInset: number,
  anchor?: THREE.Vector3
): THREE.Vector3 {
  const base = anchor ? anchor.clone() : new THREE.Vector3(0, 0, 0);
  switch (direction) {
    case "NORTH":
      // Porta no norte (-Z): mover para +Z (centro)
      return new THREE.Vector3(base.x, 0, base.z + entryInset);
    case "SOUTH":
      // Porta no sul (+Z): mover para -Z (centro)
      return new THREE.Vector3(base.x, 0, base.z - entryInset);
    case "EAST":
      // Porta no leste (+X): mover para -X (centro)
      return new THREE.Vector3(base.x - entryInset, 0, base.z);
    case "WEST":
      // Porta no oeste (-X): mover para +X (centro)
      return new THREE.Vector3(base.x + entryInset, 0, base.z);
    default:
      return base;
  }
}

function spawnOppositeToDoor(direction: DoorDirection, boundary: number): THREE.Vector3 {
  const spawnDepth = Math.max(2.5, boundary * 0.72);
  switch (direction) {
    case "NORTH":
      return new THREE.Vector3(0, 0, spawnDepth);
    case "SOUTH":
      return new THREE.Vector3(0, 0, -spawnDepth);
    case "EAST":
      return new THREE.Vector3(-spawnDepth, 0, 0);
    case "WEST":
      return new THREE.Vector3(spawnDepth, 0, 0);
    default:
      return new THREE.Vector3(0, 0, spawnDepth);
  }
}

/**
 * Calcula a posição de spawn do jogador ao entrar em uma sala do dungeon.
 *
 * Prioridade:
 * 1. Se encontrou a porta de entrada (via fromRoomId + entryDirection): spawn perto dela
 * 2. Se tem entryDirection mas sem porta correspondente: spawn perto da borda na direção de entrada
 * 3. Se é início de capítulo: spawn oposto à primeira saída (jogador olha para a aventura)
 * 4. Fallback: posição configurada da sala
 */
export function getDungeonSpawnPosition(
  room: DungeonRoom,
  fallbackSpawn: THREE.Vector3,
  context: DungeonSpawnContext | null
): THREE.Vector3 {
  const boundary = room.arenaSize / 2 - 2;
  // Inset ajustado para posicionar o jogador visivelmente NA FRENTE da porta (3 unidades adentro),
  // garantindo que ele não spawne clipado na parede ou no corredor escuro.
  const entryInset = 3;
  const safeFallback = clampToRoom(fallbackSpawn, boundary);

  if (!context) return safeFallback;

  const inbound = getInboundExit(room, context);
  if (inbound) {
    // Se a porta de entrada foi encontrada: usar sua posição como âncora.
    // Se não tem anchor (porta de retorno não existe fisicamente na sala),
    // calcular a posição esperada baseada na direção de entrada via DOOR_POSITIONS.
    const anchor = inbound.anchor ?? DOOR_POSITIONS[inbound.direction]?.(room.arenaSize);
    const spawnPos = spawnNearDoor(inbound.direction, entryInset, anchor);

    // Validação: garantir que o spawn não ficou muito longe da porta-âncora.
    // Se ficou (bug de config), recalcular com posição padrão da porta.
    if (anchor) {
      const distToAnchor = spawnPos.distanceTo(anchor);
      if (distToAnchor > Math.abs(entryInset) + 3) {
        // Recalcular usando posição padrão da porta na direção correspondente
        const defaultAnchor = DOOR_POSITIONS[inbound.direction]?.(room.arenaSize);
        if (defaultAnchor) {
          return spawnNearDoor(inbound.direction, entryInset, defaultAnchor); // bypass clamp to allow corridor spawn
        }
      }
    }

    return spawnPos; // bypass clamp to allow corridor spawn
  }

  // Se temos entryDirection mas não encontramos porta correspondente,
  // ainda assim spawnar perto da borda de entrada usando posição padrão da porta.
  if (context.entryDirection) {
    const directionAnchor = DOOR_POSITIONS[context.entryDirection]?.(room.arenaSize);
    return spawnNearDoor(context.entryDirection, entryInset, directionAnchor); // bypass clamp to allow corridor spawn
  }

  if (context.isChapterStart && room.exits.length > 0) {
    // Spawn oposto à primeira saída forward (não return paths).
    // Isso posiciona o jogador "olhando para a aventura".
    const forwardExits = room.exits.filter(e => !e.isReturnPath);
    const targetExit = forwardExits.length > 0 ? forwardExits[0] : room.exits[0];
    const exitDirection = targetExit.direction ?? inferDoorDirectionFromRotation(targetExit.rotation);
    return clampToRoom(spawnOppositeToDoor(exitDirection, boundary), boundary);
  }

  return safeFallback;
}

export function shouldSpawnEnemiesInRoom(context: DungeonEnemySpawnContext | null): boolean {
  if (!context) return true;
  if (context.forceRespawn) return true;
  return !context.roomCompleted;
}
